// ─────────────────────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────────────────────

export type Condition = 'mint' | 'excellent' | 'good' | 'fair' | 'poor'

export type Category = 'cars' | 'trucks' | 'motorcycles' | 'aircraft' | 'ships' | 'other'

export type SubmissionStatus =
  | 'pending_validation'
  | 'agent_approved'
  | 'agent_flagged'
  | 'mod_approved'
  | 'rejected'
  | 'duplicate'

export type ProductStatus = 'verified' | 'pending_review' | 'flagged'

// ─────────────────────────────────────────────────────────────────────────────
// USER
// ─────────────────────────────────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  username: string
  name: string
  avatar: string | null
  provider: string
  providerId: string | null
  createdAt: string
  updatedAt: string
}

/** Embedded in items and collections — no sensitive fields */
export interface PublicUser {
  id: string
  username: string
  name: string
  avatar: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEMS
// ─────────────────────────────────────────────────────────────────────────────

export interface UserItemImage {
  id: string
  userCollectionItemId: string
  userId: string
  /** Storage key — prepend R2_PUBLIC_URL to get a full URL */
  r2Key: string
  displayOrder: number
  isCoverImage: boolean
  createdAt: string
}

export interface UserCollectionItem {
  id: string
  userId: string
  /** null = manual entry */
  catalogProductId: string | null

  // Manual-entry fields (null on catalog-linked items)
  localName: string | null
  localBrand: string | null
  localScale: string | null
  localManufacturer: string | null
  localCarYear: number | null
  localCategory: Category | null

  // User-specific (always present)
  /** Number of physical copies owned — defaults to 1 */
  quantity: number
  condition: Condition
  purchasePrice: number | null
  /** 'YYYY-MM-DD' date string */
  purchaseDate: string | null
  tags: string[]
  personalNotes: string | null

  // Admin-set notification
  systemNote: string | null
  /** non-null means already dismissed */
  systemNoteReadAt: string | null

  createdAt: string
  updatedAt: string

  // Relations
  user: PublicUser
  catalogProduct: CatalogProduct | null
  images: UserItemImage[]
}

/** Resolved display helpers — use these instead of reading fields directly */
export function getItemDisplayName(item: UserCollectionItem): string {
  return item.catalogProduct?.name ?? item.localName ?? 'Unnamed'
}

export function getItemDisplayBrand(item: UserCollectionItem): string {
  return item.catalogProduct?.brand.name ?? item.localBrand ?? ''
}

export function getItemDisplayScale(item: UserCollectionItem): string {
  return item.catalogProduct?.scale ?? item.localScale ?? ''
}

export function getItemCoverImage(item: UserCollectionItem): UserItemImage | null {
  return item.images.find((img) => img.isCoverImage) ?? item.images[0] ?? null
}

export function hasUnreadSystemNote(item: UserCollectionItem): boolean {
  return item.systemNote !== null && item.systemNoteReadAt === null
}

// ─────────────────────────────────────────────────────────────────────────────
// CATALOG
// ─────────────────────────────────────────────────────────────────────────────

export interface CatalogBrand {
  id: string
  name: string
  slug: string
  country: string | null
  website: string | null
  logoUrl: string | null
}

/** Full detail — from /api/catalog/products/:id */
export interface CatalogProduct {
  id: string
  brandId: string
  seriesId: string | null
  productCode: string | null
  name: string
  carManufacturer: string | null
  year: string | null
  color: string | null
  scale: string
  category: string | null
  lhdRhd: string | null
  /** Full URL from scraper — no prefix needed */
  imageUrl: string | null
  source: string
  status: ProductStatus
  createdAt: string
  updatedAt: string
  brand: CatalogBrand
}

/** Lighter shape returned by catalog search */
export interface CatalogSearchResult {
  id: string
  productCode: string | null
  name: string
  year: string | null
  scale: string
  category: string | null
  /** Full URL — no prefix needed */
  imageUrl: string | null
  brand: {
    id: string
    name: string
    slug: string
    logoUrl: string | null
  }
  series: {
    id: string
    name: string
    slug: string | null
  } | null
}

export interface CatalogSubmission {
  id: string
  submittedById: string
  userCollectionItemId: string | null
  submissionType: string
  productName: string
  status: SubmissionStatus
  /** Set when submission is merged into an existing product */
  linkedProductId: string | null
  reviewerNotes: string | null
  createdAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// COLLECTIONS
// ─────────────────────────────────────────────────────────────────────────────

export interface Collection {
  id: string
  name: string
  description: string | null
  userId: string
  isPublic: boolean
  createdAt: string
  updatedAt: string
  user: PublicUser
  /** Cover images only in list view; full items in detail */
  items: UserCollectionItem[]
}

// ─────────────────────────────────────────────────────────────────────────────
// API RESPONSE WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────

export interface Pagination {
  page: number
  limit: number
  total: number
  pages: number
}

export interface ApiSuccessResponse<T> {
  success: true
  data: T
  message?: string
  pagination?: Pagination
}

export interface ApiErrorResponse {
  success: false
  error: string
  code: string
  details?: { path: string; message: string }[]
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

/** Auth response — login, signup verify, Google sign-in */
export interface AuthResponseData extends User {
  /** accessToken (short-lived, 15 min) */
  token: string
  /** refreshToken (long-lived, 7 days) */
  refreshToken: string
  /** True when a new Google account was created and the user must choose a username */
  usernameRequired?: boolean
}

/** Token refresh response */
export interface TokenRefreshData {
  accessToken: string
  refreshToken: string
}

/** User profile page response */
export interface UserProfileData extends User {
  items: UserCollectionItem[]
  collections: Collection[]
  stats: {
    totalItems: number
    totalCollections: number
  }
  pagination: {
    items: { page: number; limit: number; total: number; totalPages: number }
    collections: { page: number; limit: number; total: number; totalPages: number }
  }
}

/** Upload response */
export interface UploadData {
  url: string
  key: string
}

/** Bulk add response */
export interface BulkAddItemResult {
  index: number
  catalogProductId: string
  status: 'added' | 'already_exists' | 'not_found'
}

export interface BulkAddData {
  addedCount: number
  skippedCount: number
  results: BulkAddItemResult[]
}
